import { useEffect, useState } from "react";
import { QRCodeSVG, QRCodeCanvas } from "qrcode.react";
import { Smartphone, Download, ShieldCheck, Zap, Bell, Heart, CheckCircle2, Share2 } from "lucide-react";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const DOWNLOAD_URL = `${BACKEND}/api/app/download`;

const fmtSize = (b) => (b ? `${(b / 1048576).toFixed(0)} MB` : "");

function roundRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

function cmpVersion(a, b) {
  const pa = String(a || "").split(".").map((n) => parseInt(n, 10) || 0);
  const pb = String(b || "").split(".").map((n) => parseInt(n, 10) || 0);
  for (let i = 0; i < Math.max(pa.length, pb.length); i++) {
    const d = (pa[i] || 0) - (pb[i] || 0);
    if (d) return d;
  }
  return 0;
}

const FEATURES = [
  { icon: Zap, title: "Turant Order", desc: "Ek tap me daily essentials + electronics order karein." },
  { icon: Bell, title: "Live Notifications", desc: "Order status aur offers ki alerts seedhe phone par." },
  { icon: Heart, title: "Wishlist Sync", desc: "App aur website — dono jagah same wishlist." },
  { icon: ShieldCheck, title: "Secure & Fast", desc: "Rani Super Market ka official Android app." },
];

const STEPS = [
  "Neeche \u201cDownload for Android\u201d button dabayein — RaniSuperMarket.apk download hoga.",
  "Phone puchhe to \u201cUnknown sources / Is source se install\u201d ko allow karein.",
  "Downloaded file kholein aur \u201cInstall\u201d par tap karein.",
  "Rani Super Market app khol kar login karein — ho gaya! \ud83c\udf89",
];

export default function DownloadApp() {
  const [info, setInfo] = useState(null);
  const [pageUrl, setPageUrl] = useState("");
  const [shopUrl, setShopUrl] = useState("");
  const [installed, setInstalled] = useState({ v: "", vc: "" });

  useEffect(() => {
    document.title = "Download App — Rani Super Market";
    setPageUrl(`${window.location.origin}/download-app`);
    setShopUrl(window.location.origin);
    fetch(`${BACKEND}/api/app/info`).then((r) => r.json()).then(setInfo).catch(() => {});
    // Installed app version: the Android app opens this page with ?app_version=&app_vcode=,
    // persisted so the next plain-browser visit still knows the installed version.
    const qs = new URLSearchParams(window.location.search);
    const qv = (qs.get("app_version") || "").trim();
    const qvc = (qs.get("app_vcode") || "").trim();
    if (qv || qvc) {
      localStorage.setItem("rsmart_installed_app_version", qv);
      localStorage.setItem("rsmart_installed_app_vcode", qvc);
      setInstalled({ v: qv, vc: qvc });
    } else {
      setInstalled({ v: localStorage.getItem("rsmart_installed_app_version") || "",
                     vc: localStorage.getItem("rsmart_installed_app_vcode") || "" });
    }
  }, []);

  const latestV = info?.version || "";
  const latestVC = info?.version_code || "";
  const hasInstalled = !!(installed.v || installed.vc);
  const updateAvailable = !!(info && info.available !== false && hasInstalled && (
    (installed.vc && latestVC && Number(installed.vc) < Number(latestVC)) ||
    (installed.v && latestV && cmpVersion(installed.v, latestV) < 0)
  ));
  const upToDate = !!(info && info.available !== false && hasInstalled && !updateAvailable);

  const downloadPoster = async () => {
    const W = 1080, H = 1350;
    const c = document.createElement("canvas"); c.width = W; c.height = H;
    const ctx = c.getContext("2d");
    const g = ctx.createLinearGradient(0, 0, 0, H);
    g.addColorStop(0, "#047857"); g.addColorStop(1, "#0f766e");
    ctx.fillStyle = g; ctx.fillRect(0, 0, W, H);
    ctx.textAlign = "center";
    // logo card
    roundRect(ctx, W / 2 - 150, 55, 300, 150, 28); ctx.fillStyle = "#ffffff"; ctx.fill();
    const logo = new Image(); logo.src = "/rani-logo.png";
    try { await logo.decode(); } catch (e) { console.warn("poster logo load failed", e); }
    if (logo.naturalWidth) {
      const r = Math.min(260 / logo.naturalWidth, 118 / logo.naturalHeight);
      const lw = logo.naturalWidth * r, lh = logo.naturalHeight * r;
      ctx.drawImage(logo, W / 2 - lw / 2, 55 + (150 - lh) / 2, lw, lh);
    }
    // heading
    ctx.fillStyle = "#ffffff"; ctx.font = "800 66px Arial";
    ctx.fillText("Rani Super Market", W / 2, 300);
    ctx.fillStyle = "#d1fae5"; ctx.font = "600 34px Arial";
    ctx.fillText("Quality, Guarantee Ke Saath", W / 2, 352);
    // QR card
    roundRect(ctx, 190, 410, 700, 700, 40); ctx.fillStyle = "#ffffff"; ctx.fill();
    const qr = document.getElementById("shop-qr-canvas");
    if (qr) ctx.drawImage(qr, 190 + 80, 410 + 55, 540, 540);
    ctx.fillStyle = "#065f46"; ctx.font = "800 46px Arial";
    ctx.fillText("Scan to Shop Online", W / 2, 410 + 655);
    // caption
    ctx.fillStyle = "#ffffff"; ctx.font = "600 37px Arial";
    ctx.fillText("Ghar baithe order karein — daily essentials,", W / 2, 1185);
    ctx.fillText("electronics & POS hardware.", W / 2, 1233);
    // url pill
    roundRect(ctx, W / 2 - 270, 1270, 540, 62, 31); ctx.fillStyle = "#facc15"; ctx.fill();
    ctx.fillStyle = "#064e3b"; ctx.font = "800 36px Arial";
    ctx.fillText(shopUrl.replace(/^https?:\/\//, ""), W / 2, 1312);
    c.toBlob((blob) => {
      if (!blob) return;
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = "rani-super-market-shop-qr-poster.png";
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success("Poster download ho gaya!");
    }, "image/png");
  };

  const share = async () => {
    const url = `${window.location.origin}/download-app`;
    const text = "Rani Super Market Android app download karein \u2014 daily essentials + electronics ghar tak!";
    try {
      if (navigator.share) await navigator.share({ title: "Rani Super Market App", text, url });
      else { await navigator.clipboard.writeText(url); toast.success("Link copy ho gaya!"); }
    } catch (e) { if (e?.name !== "AbortError") console.warn("share failed", e); }
  };

  return (
    <div className="min-h-screen bg-[#f1f3f6]" data-testid="download-app-page">
      {/* Hero */}
      <section className="relative overflow-hidden bg-gradient-to-br from-emerald-700 via-emerald-600 to-teal-600 text-white">
        <div className="absolute -top-16 -right-16 w-72 h-72 bg-white/10 rounded-full blur-2xl" />
        <div className="absolute -bottom-20 -left-10 w-72 h-72 bg-teal-400/20 rounded-full blur-3xl" />
        <div className="relative max-w-6xl mx-auto px-4 py-12 lg:py-16 grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <span className="inline-flex items-center gap-1.5 bg-white/15 backdrop-blur px-3 py-1 rounded-full text-xs font-bold tracking-wide">
              <Smartphone size={14} /> OFFICIAL ANDROID APP
            </span>
            <h1 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-black leading-tight">
              Rani Super Market<br />ab aapke phone me
            </h1>
            <p className="mt-4 text-emerald-50 text-base max-w-md leading-relaxed">
              Poora bazaar ek app me — daily essentials, electronics aur kitchen provisions.
              Order karein, offers paayein aur wishlist kahin bhi sync karein. <span className="font-semibold text-white">Bilkul free.</span>
            </p>
            {updateAvailable && (
              <div className="mt-5 max-w-md rounded-2xl bg-amber-300 text-amber-950 p-4 shadow-lg" data-testid="app-update-banner">
                <div className="flex items-center gap-2 font-black text-sm"><Bell size={16} /> Naya version aaya hai!</div>
                <p className="text-xs mt-1 font-medium">
                  Aapke phone me v{installed.v || installed.vc} hai — latest <b>v{latestV}{latestVC ? ` (${latestVC})` : ""}</b> available hai.
                </p>
                <a href={DOWNLOAD_URL} data-testid="app-update-btn" className="mt-3 inline-flex items-center gap-2 bg-amber-950 text-amber-200 font-bold text-xs px-4 py-2.5 rounded-xl hover:bg-amber-900 active:scale-95 transition-all">
                  <Download size={14} /> Update Now {info?.size_bytes ? `· ${fmtSize(info.size_bytes)}` : ""}
                </a>
              </div>
            )}
            {upToDate && (
              <p className="mt-5 inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-100 bg-white/10 px-3 py-1.5 rounded-full" data-testid="app-uptodate-badge">
                <CheckCircle2 size={13} /> Aapka app latest version (v{installed.v || installed.vc}) par hai
              </p>
            )}
            <div className="mt-7 flex flex-wrap items-center gap-3">
              <a href={DOWNLOAD_URL} data-testid="apk-download-btn"
                className="inline-flex items-center gap-2.5 bg-white text-emerald-700 font-black px-6 py-3.5 rounded-xl shadow-lg shadow-emerald-900/20 hover:scale-[1.03] active:scale-95 transition-transform">
                <Download size={20} /> Download for Android
              </a>
              <button onClick={share} data-testid="apk-share-btn"
                className="inline-flex items-center gap-2 bg-emerald-800/40 border border-white/30 text-white font-semibold px-4 py-3 rounded-xl hover:bg-emerald-800/60 transition-colors">
                <Share2 size={17} /> Share
              </button>
            </div>
            <div className="mt-3 text-emerald-100/90 text-xs flex flex-wrap gap-x-4 gap-y-1">
              <span>Android {info?.version && `• v${info.version}`}</span>
              {info?.size_bytes && <span>• {fmtSize(info.size_bytes)} APK</span>}
              <span>• 100% Safe &amp; Official</span>
            </div>
          </div>

          {/* QR card */}
          <div className="justify-self-center lg:justify-self-end">
            <div className="bg-white rounded-3xl p-6 sm:p-7 shadow-2xl shadow-emerald-900/30 text-center w-[280px]" data-testid="apk-qr-card">
              <div className="text-slate-800 font-black text-sm">Scan &amp; Install</div>
              <p className="text-slate-500 text-[11px] mt-1 mb-4 leading-snug">
                Phone ke camera se ye QR scan karein aur seedha app download karein
              </p>
              <div className="inline-flex p-3 rounded-2xl bg-white ring-1 ring-slate-200">
                {pageUrl && (
                  <QRCodeSVG value={pageUrl} size={188} level="M" marginSize={1}
                    fgColor="#065f46" bgColor="#ffffff"
                    imageSettings={{ src: "/rani-logo.png", height: 34, width: 34, excavate: true }} />
                )}
              </div>
              <div className="mt-4 flex items-center justify-center gap-1.5 text-emerald-700 text-[11px] font-semibold">
                <CheckCircle2 size={14} /> Camera se scan karte hi khul jayega
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="max-w-6xl mx-auto px-4 py-10">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {FEATURES.map((f) => (
            <div key={f.title} className="bg-white rounded-2xl p-5 border border-slate-100 hover:shadow-md hover:-translate-y-0.5 transition-all">
              <div className="w-11 h-11 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center mb-3">
                <f.icon size={22} />
              </div>
              <div className="font-bold text-slate-800 text-sm">{f.title}</div>
              <div className="text-slate-500 text-xs mt-1 leading-relaxed">{f.desc}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Shop QR Poster (printable) */}
      <section className="max-w-6xl mx-auto px-4 pb-4" data-testid="shop-qr-poster-section">
        <div className="bg-white rounded-3xl border border-slate-100 p-6 sm:p-8 lg:grid lg:grid-cols-5 gap-8 items-center">
          <div className="lg:col-span-2">
            <span className="inline-flex items-center gap-1.5 bg-emerald-50 text-emerald-700 px-3 py-1 rounded-full text-xs font-bold">FOR YOUR SHOP</span>
            <h2 className="mt-3 text-lg font-black text-slate-800">Shop QR Poster</h2>
            <p className="text-slate-500 text-sm mt-1 leading-relaxed">Apni dukaan par lagane ke liye ready-made poster. Customer QR scan kar ke seedhe aapka online store khol lega aur ghar baithe order karega.</p>
            <button onClick={downloadPoster} data-testid="poster-download-btn"
              className="mt-5 inline-flex items-center gap-2 bg-emerald-600 text-white font-black px-5 py-3 rounded-xl hover:bg-emerald-700 active:scale-95 transition-all">
              <Download size={18} /> Download Poster (PNG)
            </button>
            <p className="text-[11px] text-slate-400 mt-3">Tip: A4 par print karke counter, entrance ya dukaan ke door par laga dein.</p>
          </div>
          <div className="lg:col-span-3 flex justify-center mt-6 lg:mt-0">
            <div data-testid="poster-preview" className="w-[280px] rounded-2xl overflow-hidden shadow-xl" style={{ background: "linear-gradient(180deg,#047857,#0f766e)" }}>
              <div className="pt-5 flex justify-center">
                <div className="bg-white rounded-xl px-4 py-2"><img src="/rani-logo.png" alt="Rani Super Market" className="h-10 w-auto object-contain" /></div>
              </div>
              <div className="text-center text-white pt-3 px-4">
                <div className="font-black text-lg leading-tight">Rani Super Market</div>
                <div className="text-emerald-100 text-[11px]">Quality, Guarantee Ke Saath</div>
              </div>
              <div className="flex justify-center py-4">
                <div className="bg-white rounded-2xl p-3 text-center">
                  {shopUrl && <QRCodeSVG value={shopUrl} size={168} level="M" marginSize={1} fgColor="#065f46" imageSettings={{ src: "/rani-logo.png", height: 30, width: 30, excavate: true }} />}
                  <div className="text-emerald-800 font-black text-xs mt-2">Scan to Shop Online</div>
                </div>
              </div>
              <div className="text-center text-white text-[11px] px-4 pb-2">Ghar baithe order karein — daily essentials, electronics &amp; POS hardware.</div>
              <div className="text-center pb-5"><span className="inline-block bg-yellow-400 text-emerald-900 font-black text-[11px] px-3 py-1 rounded-full break-all">{shopUrl.replace(/^https?:\/\//, "")}</span></div>
            </div>
          </div>
        </div>
      </section>
      {/* hidden QR canvas used to rasterize the poster PNG */}
      <div style={{ position: "absolute", left: -9999, top: -9999 }} aria-hidden>
        {shopUrl && <QRCodeCanvas id="shop-qr-canvas" value={shopUrl} size={560} level="M" marginSize={1} fgColor="#065f46" imageSettings={{ src: "/rani-logo.png", height: 90, width: 90, excavate: true }} />}
      </div>

      {/* Install steps + secondary download */}
      <section className="max-w-6xl mx-auto px-4 pb-16">
        <div className="bg-white rounded-3xl border border-slate-100 overflow-hidden lg:grid lg:grid-cols-5">
          <div className="lg:col-span-3 p-6 sm:p-8">
            <h2 className="text-lg font-black text-slate-800">Install kaise karein?</h2>
            <p className="text-slate-500 text-sm mt-1">Sirf 4 aasaan steps — 1 minute me app ready.</p>
            <ol className="mt-5 space-y-4">
              {STEPS.map((s, i) => (
                <li key={i} className="flex gap-3">
                  <span className="shrink-0 w-7 h-7 rounded-full bg-emerald-600 text-white text-xs font-black flex items-center justify-center">{i + 1}</span>
                  <span className="text-slate-700 text-sm leading-relaxed pt-0.5">{s}</span>
                </li>
              ))}
            </ol>
          </div>
          <div className="lg:col-span-2 bg-gradient-to-br from-slate-900 to-slate-800 text-white p-6 sm:p-8 flex flex-col justify-center">
            <div className="text-emerald-400 font-black text-xs tracking-widest">READY TO GO</div>
            <div className="text-2xl font-black mt-2 leading-tight">Aaj hi download<br />karein</div>
            <p className="text-slate-300 text-sm mt-2">Quality, guarantee ke saath — seedhe aapke phone par.</p>
            <a href={DOWNLOAD_URL} data-testid="apk-download-btn-2"
              className="mt-5 inline-flex items-center justify-center gap-2 bg-emerald-500 text-white font-black px-5 py-3.5 rounded-xl hover:bg-emerald-400 active:scale-95 transition-all">
              <Download size={19} /> Download APK {info?.size_bytes ? `(${fmtSize(info.size_bytes)})` : ""}
            </a>
            <p className="text-slate-500 text-[11px] mt-3 leading-relaxed">
              Ye Rani Super Market ka official Android app hai. Play Store ke bahar install ho raha hai isliye phone "Unknown source" ki permission maang sakta hai — allow kar dein.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
